/* CatalogSetupPreview — NON-FUNCTIONAL prototype for the seller.
 *
 * Public preview at /preview/catalog-setup. Lets the seller compare two ways
 * to control how their catalog looks + which fields show:
 *   Option A — "Customize catalog" quick slide-in panel (instant edits)
 *   Option B — "Smart wizard" that remembers your place + jump to any step
 *
 * Pure mock state. Nothing is saved. Nothing touches the live catalog. */
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../../components/ui/sheet";
import { toast } from "sonner";
import {
  Sliders, Wand2, LayoutGrid, Table2, Eye, EyeOff, Plus, Check,
  ArrowRight, ArrowLeft, Package, Store, ShoppingBag, X, Sparkles,
  CheckCircle2, Circle, Save, ListChecks, Factory,
} from "lucide-react";

const FIELDS = [
  { key: "image",        label: "Product image",   locked: false },
  { key: "name",         label: "Product name",    locked: true  },
  { key: "sku",          label: "SKU",             locked: false },
  { key: "category",     label: "Category",        locked: false },
  { key: "price",        label: "Wholesale price", locked: true  },
  { key: "retail_price", label: "Retail / MSRP",   locked: false },
  { key: "map_price",    label: "MAP price",       locked: false },
  { key: "moq",          label: "MOQ",             locked: false },
  { key: "stock",        label: "Stock on hand",   locked: false },
  { key: "description",  label: "Description",     locked: false },
  { key: "brand",        label: "Brand",           locked: false },
];

const DEFAULT_VIS = {
  image: true, name: true, sku: true, category: true, price: true,
  retail_price: false, map_price: false, moq: true, stock: true,
  description: true, brand: true,
};

const SAMPLE = {
  name: "Hydra-Glow Vitamin C Serum",
  sku: "HG-VC-30",
  category: "Skincare",
  price: 12.5,
  retail_price: 29.0,
  map_price: 24.0,
  moq: 12,
  stock: 340,
  brand: "GlowCo",
  description: "30ml brightening serum with 10% vitamin C and hyaluronic acid.",
  image: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=400&q=60",
};

export default function CatalogSetupPreview() {
  const [openA, setOpenA] = useState(false);
  const [openB, setOpenB] = useState(false);

  return (
    <div className="min-h-screen bg-[#fafaf9] text-[#0d0d0d]">
      {/* Top banner */}
      <div className="bg-foreground text-background">
        <div className="max-w-5xl mx-auto px-6 py-2.5 flex items-center gap-2 text-[12px]">
          <Sparkles className="w-3.5 h-3.5" />
          Preview only — nothing here is saved or connected to your live catalog. Pick the approach you like and I'll build it for real.
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/50">Catalog setup · choose an approach</div>
        <h1 className="display text-4xl sm:text-5xl mt-2 tracking-tight">How should sellers control their catalog?</h1>
        <p className="text-[15px] text-foreground/65 mt-4 max-w-2xl leading-relaxed">
          Both options use the engine you already have (layout templates, field visibility, custom fields).
          The difference is the <strong>experience</strong> for editing it later. Open each demo and click around.
        </p>

        <div className="grid md:grid-cols-2 gap-6 mt-10">
          {/* Option A */}
          <OptionCard
            tag="Option A"
            icon={Sliders}
            title="Quick Customize panel"
            blurb="A slide-in panel right on the Catalog page. Toggle fields, switch layout, see a live preview — saves instantly, no wizard, no restart."
            pros={[
              "One click from the catalog — always available",
              "Instant edits with a live 'as buyers see it' preview",
              "Best for ongoing tweaks (hide a price, add a field)",
            ]}
            cons={["Less hand-holding for first-time setup"]}
            cta="Open the panel demo"
            onOpen={() => setOpenA(true)}
            testid="open-option-a"
          />

          {/* Option B */}
          <OptionCard
            tag="Option B"
            icon={Wand2}
            title="Smart wizard (remembers your place)"
            blurb="The guided 4-step wizard — but it remembers where you left off and lets you jump straight to any step to edit. No more restarting at step 1."
            pros={[
              "Guided, great for first-time setup",
              "Jump to any step to edit later",
              "Resumes exactly where you left off",
            ]}
            cons={["A few more clicks than an inline panel"]}
            cta="Open the wizard demo"
            onOpen={() => setOpenB(true)}
            testid="open-option-b"
          />
        </div>

        <div className="mt-10 rounded-2xl border border-border bg-white p-5 text-[13px] text-foreground/65">
          <div className="font-medium text-foreground mb-1">My recommendation</div>
          Build <strong>Option A</strong> for day-to-day editing and keep the wizard (Option B's "remembers your place" upgrade) for first-time onboarding — they complement each other. But your call: tell me A, B, or both.
        </div>
      </div>

      <PanelDemoA open={openA} onOpenChange={setOpenA} />
      <WizardDemoB open={openB} onOpenChange={setOpenB} />
    </div>
  );
}

function OptionCard({ tag, icon: Icon, title, blurb, pros, cons, cta, onOpen, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-6 flex flex-col">
      <div className="flex items-center gap-2">
        <span className="w-9 h-9 rounded-xl bg-foreground text-background grid place-items-center"><Icon className="w-4 h-4" /></span>
        <span className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/45">{tag}</span>
      </div>
      <h2 className="display text-2xl mt-4 tracking-tight">{title}</h2>
      <p className="text-[13px] text-foreground/60 mt-2 leading-relaxed">{blurb}</p>
      <div className="mt-4 space-y-1.5">
        {pros.map(p => (
          <div key={p} className="flex items-start gap-2 text-[12.5px] text-foreground/75">
            <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />{p}
          </div>
        ))}
        {cons.map(c => (
          <div key={c} className="flex items-start gap-2 text-[12.5px] text-foreground/45">
            <X className="w-3.5 h-3.5 mt-0.5 shrink-0" />{c}
          </div>
        ))}
      </div>
      <Button onClick={onOpen} data-testid={testid}
        className="mt-6 rounded-full h-11 bg-foreground text-background hover:bg-foreground/90">
        {cta}<ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </div>
  );
}

/* ─────────────────────── Option A — quick panel ─────────────────────── */
function PanelDemoA({ open, onOpenChange }) {
  const [tab, setTab] = useState("layout");
  const [layout, setLayout] = useState("grid");
  const [vis, setVis] = useState(DEFAULT_VIS);
  const [catalogOn, setCatalogOn] = useState(true);

  const toggle = (k) => setVis(v => ({ ...v, [k]: !v[k] }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl p-0 flex flex-col" data-testid="panel-a">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <SheetTitle className="flex items-center gap-2"><Sliders className="w-4 h-4" />Customize catalog</SheetTitle>
          <div className="flex gap-1 mt-3">
            {[["layout", "Layout", LayoutGrid], ["fields", "Fields", ListChecks], ["visibility", "Visibility", Eye]].map(([k, l, I]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`panel-a-tab-${k}`}
                className={`inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-[12.5px] transition-colors ${tab === k ? "bg-foreground text-background" : "bg-secondary text-foreground/65 hover:text-foreground"}`}>
                <I className="w-3.5 h-3.5" />{l}
              </button>
            ))}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tab === "layout" && (
            <div className="grid grid-cols-2 gap-3">
              {[["grid", "Catalog grid", "Image-led cards. Great for visual brands.", LayoutGrid],
                ["linesheet", "Linesheet", "Dense table. Great for big SKU counts.", Table2]].map(([k, t, d, I]) => (
                <button key={k} onClick={() => setLayout(k)} data-testid={`panel-a-layout-${k}`}
                  className={`rounded-xl border p-4 text-left transition-colors ${layout === k ? "border-foreground bg-foreground text-background" : "border-border bg-white hover:border-foreground/40"}`}>
                  <I className="w-5 h-5" />
                  <div className="font-medium text-sm mt-3">{t}</div>
                  <div className={`text-[12px] mt-1 ${layout === k ? "text-background/70" : "text-foreground/55"}`}>{d}</div>
                </button>
              ))}
            </div>
          )}

          {tab === "fields" && (
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-1">
                {FIELDS.map(f => (
                  <div key={f.key} className="flex items-center justify-between py-1.5">
                    <div className="text-[13px] flex items-center gap-1.5">
                      {f.label}
                      {f.locked && <span className="text-[10px] mono text-foreground/35 uppercase">required</span>}
                    </div>
                    <Switch checked={!!vis[f.key]} disabled={f.locked}
                      onCheckedChange={() => toggle(f.key)} data-testid={`panel-a-field-${f.key}`} />
                  </div>
                ))}
                <button className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] text-foreground/60 hover:text-foreground">
                  <Plus className="w-3.5 h-3.5" />Add custom field (e.g. "Country of origin")
                </button>
              </div>
              <div>
                <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/45 mb-2">Live preview · as buyers see it</div>
                <PreviewCard vis={vis} layout={layout} />
              </div>
            </div>
          )}

          {tab === "visibility" && (
            <div className="space-y-3">
              <ToggleRow icon={Store} title="Catalog app" desc="Connected buyers can browse your catalog in their portal"
                checked={catalogOn} onChange={() => setCatalogOn(v => !v)} testid="panel-a-catalog-on" />
              <ToggleRow icon={ShoppingBag} title="Public storefront" desc="Anyone with your /s/ link can browse + order (guests sign up at checkout)"
                checked={catalogOn} onChange={() => {}} testid="panel-a-storefront-on" />
            </div>
          )}
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border">
          <Button onClick={() => { toast.success("Saved (preview only — nothing was changed)"); }}
            data-testid="panel-a-save"
            className="w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
            <Save className="w-4 h-4 mr-2" />Save changes
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function ToggleRow({ icon: Icon, title, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-white p-4">
      <div className="flex items-start gap-3">
        <Icon className="w-4 h-4 text-foreground/55 mt-0.5" />
        <div>
          <div className="font-medium text-sm">{title}</div>
          <div className="text-[12px] text-foreground/55 mt-0.5">{desc}</div>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function PreviewCard({ vis, layout }) {
  if (layout === "linesheet") {
    return (
      <div className="rounded-xl border border-border bg-white overflow-hidden text-[12px]">
        <div className="grid grid-cols-[1fr_auto] gap-2 px-3 py-2 bg-secondary/60 font-medium">
          <span>Product</span><span>Price</span>
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-2 px-3 py-2.5 items-center">
          <div>
            <div className="font-medium">{SAMPLE.name}</div>
            <div className="text-foreground/50 mono text-[11px]">
              {vis.sku && `SKU ${SAMPLE.sku}`}{vis.category && ` · ${SAMPLE.category}`}{vis.moq && ` · MOQ ${SAMPLE.moq}`}
            </div>
          </div>
          <div className="text-right">
            <div className="display text-base">${SAMPLE.price.toFixed(2)}</div>
            {vis.retail_price && <div className="text-[10px] text-foreground/45">MSRP ${SAMPLE.retail_price.toFixed(0)}</div>}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden max-w-[240px]">
      <div className="aspect-[4/3] bg-secondary overflow-hidden grid place-items-center">
        {vis.image
          ? <img src={SAMPLE.image} alt={SAMPLE.name} className="w-full h-full object-cover" />
          : <Package className="w-8 h-8 text-foreground/25" />}
      </div>
      <div className="p-3">
        {vis.category && <div className="text-[10px] mono uppercase tracking-wide text-foreground/45">{SAMPLE.category}</div>}
        <div className="font-medium text-[13px] mt-0.5 leading-snug">{SAMPLE.name}</div>
        {vis.brand && <div className="text-[11px] text-foreground/50 mt-0.5">{SAMPLE.brand}</div>}
        {vis.sku && <div className="text-[11px] mono text-foreground/40 mt-0.5">SKU {SAMPLE.sku}</div>}
        {vis.description && <div className="text-[11px] text-foreground/55 mt-1 line-clamp-2">{SAMPLE.description}</div>}
        <div className="flex items-end justify-between mt-2">
          <div>
            <div className="display text-lg">${SAMPLE.price.toFixed(2)}</div>
            {vis.moq && <div className="text-[10px] text-foreground/45 mono">MOQ {SAMPLE.moq}</div>}
          </div>
          <div className="text-right">
            {vis.retail_price && <div className="text-[10px] text-foreground/45">MSRP ${SAMPLE.retail_price.toFixed(0)}</div>}
            {vis.map_price && <div className="text-[10px] text-foreground/45">MAP ${SAMPLE.map_price.toFixed(0)}</div>}
            {vis.stock && <div className="text-[10px] text-emerald-600">{SAMPLE.stock} in stock</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Option B — smart wizard demo ──────────────────── */
const STEPS = [
  { key: "industry", label: "Industry", icon: Factory },
  { key: "template", label: "Layout",   icon: LayoutGrid },
  { key: "fields",   label: "Fields",   icon: ListChecks },
  { key: "finish",   label: "Finish",   icon: CheckCircle2 },
];

function WizardDemoB({ open, onOpenChange }) {
  const [active, setActive] = useState("template"); // demo: resumes mid-way
  const [done] = useState(new Set(["industry", "template"]));
  const [layout, setLayout] = useState("grid");
  const [vis, setVis] = useState(DEFAULT_VIS);
  const idx = STEPS.findIndex(s => s.key === active);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] p-0 flex flex-col" data-testid="wizard-b">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <SheetTitle className="flex items-center gap-2"><Wand2 className="w-4 h-4" />Catalog setup wizard</SheetTitle>
          <div className="inline-flex items-center gap-1.5 text-[11.5px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 mt-2 w-fit">
            <Check className="w-3 h-3" />Resumed where you left off · jump to any step to edit
          </div>
        </SheetHeader>

        {/* Clickable stepper */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => {
              const isDone = done.has(s.key);
              const isActive = s.key === active;
              return (
                <div key={s.key} className="flex items-center gap-2 flex-1">
                  <button onClick={() => setActive(s.key)} data-testid={`wizard-b-step-${s.key}`}
                    className={`flex items-center gap-2 px-3 h-9 rounded-full text-[12.5px] transition-colors w-full justify-center ${
                      isActive ? "bg-foreground text-background"
                      : isDone ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                      : "bg-secondary text-foreground/55 hover:text-foreground"}`}>
                    {isDone && !isActive ? <CheckCircle2 className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
                    {s.label}
                  </button>
                  {i < STEPS.length - 1 && <div className="w-4 h-px bg-border shrink-0" />}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 max-w-3xl mx-auto w-full">
          {active === "industry" && (
            <StepShell title="Pick your industry" desc="We preload sensible defaults for your category.">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {["Beauty & skincare", "Apparel", "Food & beverage", "Home goods", "Accessories", "Other"].map(x => (
                  <div key={x} className={`rounded-xl border p-4 text-sm ${x === "Beauty & skincare" ? "border-foreground bg-foreground text-background" : "border-border bg-white"}`}>{x}</div>
                ))}
              </div>
            </StepShell>
          )}
          {active === "template" && (
            <StepShell title="Choose a layout" desc="How buyers see your catalog. Change it anytime.">
              <div className="grid grid-cols-2 gap-3">
                {[["grid", "Catalog grid", LayoutGrid], ["linesheet", "Linesheet", Table2]].map(([k, t, I]) => (
                  <button key={k} onClick={() => setLayout(k)} data-testid={`wizard-b-layout-${k}`}
                    className={`rounded-xl border p-5 text-left ${layout === k ? "border-foreground bg-foreground text-background" : "border-border bg-white hover:border-foreground/40"}`}>
                    <I className="w-6 h-6" /><div className="font-medium mt-3">{t}</div>
                  </button>
                ))}
              </div>
              <div className="mt-5"><PreviewCard vis={vis} layout={layout} /></div>
            </StepShell>
          )}
          {active === "fields" && (
            <StepShell title="Choose what buyers see" desc="Toggle fields. Live preview on the right.">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-1">
                  {FIELDS.map(f => (
                    <div key={f.key} className="flex items-center justify-between py-1.5">
                      <div className="text-[13px]">{f.label}{f.locked && <span className="text-[10px] mono text-foreground/35 uppercase ml-1.5">required</span>}</div>
                      <Switch checked={!!vis[f.key]} disabled={f.locked} onCheckedChange={() => setVis(v => ({ ...v, [f.key]: !v[f.key] }))} data-testid={`wizard-b-field-${f.key}`} />
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/45 mb-2">Live preview</div>
                  <PreviewCard vis={vis} layout={layout} />
                </div>
              </div>
            </StepShell>
          )}
          {active === "finish" && (
            <StepShell title="You're all set" desc="Your catalog is configured. Edit any step anytime.">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-[13px] text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />Setup complete — buyers now see your catalog the way you configured it.
              </div>
            </StepShell>
          )}
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border flex-row justify-between sm:justify-between">
          <Button variant="outline" className="rounded-full" disabled={idx === 0}
            onClick={() => setActive(STEPS[Math.max(0, idx - 1)].key)} data-testid="wizard-b-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" />Back
          </Button>
          {active === "finish" ? (
            <Button onClick={() => toast.success("Done (preview only)")} className="rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="wizard-b-finish">
              Finish<Check className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button onClick={() => setActive(STEPS[Math.min(STEPS.length - 1, idx + 1)].key)} className="rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="wizard-b-next">
              Next<ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function StepShell({ title, desc, children }) {
  return (
    <div>
      <h3 className="display text-2xl tracking-tight">{title}</h3>
      <p className="text-[13px] text-foreground/60 mt-1.5 mb-6">{desc}</p>
      {children}
    </div>
  );
}
